import React, { useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKSocialButton from "components/MKSocialButton";
import MKBadge from "components/MKBadge";

// Material Kit 2 PRO React examples
// import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Custom components
import ReservationForm from "./components/ReservationForm";
import ImageCarousel from "./components/ImageCarousel";
import MenuItemDialog from "./components/MenuItemDialog";

// Routes
// import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import comienzoLogo from "assets/images/comienzo-logo.png";

const bgImage =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop";

// Menu items data
const menuItems = [
  {
    name: "Carne Asada",
    description:
      "Grilled marinated beef served with Mexican rice, refried beans, and handmade tortillas",
    image: "https://images.unsplash.com/photo-1615870216519-2f9fa575fa5c?q=80&w=600&h=400&fit=crop",
    category: "Mains",
    price: "RM 38",
    badge: "Popular",
  },
  {
    name: "Pollo Asada",
    description:
      "Grilled chicken marinated in citrus and Mexican spices, served with roasted vegetables",
    image: "https://images.unsplash.com/photo-1633321702518-7feccafb94d5?q=80&w=600&h=400&fit=crop",
    category: "Mains",
    price: "RM 32",
  },
  {
    name: "Verde Enchiladas",
    description:
      "Corn tortillas filled with chicken, topped with green chile sauce and melted cheese",
    image: "https://images.unsplash.com/photo-1534352956036-cd81e27dd615?q=80&w=600&h=400&fit=crop",
    category: "Mains",
    price: "RM 28",
    badge: "Chef's Special",
  },
  {
    name: "Huevos Rancheros",
    description:
      "Fried eggs served on corn tortillas with ranchero sauce, avocado, and refried beans",
    image: "https://images.unsplash.com/photo-1608039829572-78524f79c4c7?q=80&w=600&h=400&fit=crop",
    category: "Breakfast",
    price: "RM 25",
  },
  {
    name: "Classic Chicken Parm",
    description: "Italian-style breaded chicken breast topped with marinara and melted mozzarella",
    image: "https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?q=80&w=600&h=400&fit=crop",
    category: "Italian Fusion",
    price: "RM 35",
    badge: "New",
  },
  {
    name: "Birria Tacos",
    description: "Slow-cooked beef tacos served with consommé for dipping and lime wedges",
    image: "https://images.unsplash.com/photo-1624300629298-e9de39c13be5?q=80&w=600&h=400&fit=crop",
    category: "Tacos",
    price: "RM 30",
    badge: "Spicy",
  },
];

function RestaurantLanding() {
  const [openReservation, setOpenReservation] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  return (
    <>
      {/* <DefaultNavbar routes={routes} sticky /> */}
      <MKBox
        minHeight="100vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.75),
              rgba(gradients.dark.state, 0.75)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            mx="auto"
            textAlign="center"
            flexDirection="column"
            spacing={3}
          >
            <Grid item>
              <MKBox
                component="img"
                src={comienzoLogo}
                alt="Comienzo Logo"
                width={{ xs: "200px", md: "300px" }}
                mx="auto"
                mb={2}
                sx={{
                  animation: "fadeIn 2s ease-out",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  padding: "16px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              />
              <MKTypography
                variant="h1"
                color="white"
                sx={({ breakpoints, typography: { size } }) => ({
                  [breakpoints.down("md")]: {
                    fontSize: size["3xl"],
                  },
                  textShadow: "2px 2px 4px rgba(0,0,0,0.4)",
                  letterSpacing: "0.2em",
                  animation: "fadeIn 2s ease-out",
                  "@keyframes fadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                })}
              >
                Comienzo
              </MKTypography>
            </Grid>
            <Grid item>
              <MKTypography
                variant="h3"
                color="white"
                sx={{
                  textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
                  letterSpacing: "0.1em",
                  animation: "slideIn 1.5s ease-out 0.5s both",
                  "@keyframes slideIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateX(-20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                Mexican & Italian Fusion
              </MKTypography>
              <MKTypography
                variant="body1"
                color="white"
                mt={1}
                mb={3}
                sx={{
                  textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                  fontSize: "1.2rem",
                  animation: "fadeUp 1.5s ease-out 1s both",
                  "@keyframes fadeUp": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                Where Mexican flavors meet Italian traditions
              </MKTypography>
              <MKButton
                variant="contained"
                color="info"
                size="large"
                onClick={() => setOpenReservation(true)}
                sx={{
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  transition: "all 0.3s ease-in-out",
                  animation: "pulseButton 2s infinite",
                  "&:hover": {
                    transform: "translateY(-2px) scale(1.05)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.2)",
                  },
                  "@keyframes pulseButton": {
                    "0%": {
                      transform: "scale(1)",
                    },
                    "50%": {
                      transform: "scale(1.05)",
                    },
                    "100%": {
                      transform: "scale(1)",
                    },
                  },
                }}
              >
                Make a Reservation
              </MKButton>
            </Grid>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: { xs: 2, md: 4 },
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          background: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.9),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.1)",
          borderRadius: "xl",
          overflow: "hidden",
        }}
      >
        <Container>
          <Grid container spacing={4}>
            {/* Our Story Section */}
            <Grid item xs={12}>
              <MKBox
                p={4}
                borderRadius="xl"
                sx={{
                  background: "white",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <MKTypography
                  variant="overline"
                  color="info"
                  sx={{
                    opacity: 0.8,
                    letterSpacing: 2,
                  }}
                >
                  Our Story
                </MKTypography>
                <MKTypography variant="h2" mt={1}>
                  A Fusion of Flavors
                </MKTypography>
                <MKTypography
                  variant="body1"
                  color="text"
                  mt={2}
                  sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}
                >
                  At Comienzo, we blend the vibrant flavors of Mexican cuisine with the refined
                  traditions of Italian cooking. Our fusion concept brings you the best of both
                  worlds, creating unique dishes that celebrate the rich culinary heritage of both
                  cultures.
                </MKTypography>
              </MKBox>
            </Grid>

            {/* Carousel and Opening Hours Section */}
            <Grid item xs={12}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <MKBox height="500px">
                    <ImageCarousel />
                  </MKBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MKBox
                    height="500px"
                    p={4}
                    borderRadius="xl"
                    sx={{
                      background: "white",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      overflowY: "auto",
                    }}
                  >
                    <MKTypography
                      variant="overline"
                      color="info"
                      sx={{
                        opacity: 0.8,
                        letterSpacing: 2,
                      }}
                    >
                      Visit Us
                    </MKTypography>
                    <MKTypography variant="h4" mt={1} mb={3}>
                      Opening Hours
                    </MKTypography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MKBox
                          p={2.5}
                          sx={{
                            borderRadius: "lg",
                            border: "1px solid",
                            borderColor: ({ palette: { grey } }) => grey[200],
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "info.main",
                              transform: "translateX(8px)",
                            },
                          }}
                        >
                          <MKTypography variant="h6" mb={0.5}>
                            Tuesday - Sunday
                          </MKTypography>
                          <MKTypography variant="body2" color="text" sx={{ fontSize: "1rem" }}>
                            12:00 PM - 10:00 PM
                          </MKTypography>
                        </MKBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MKBox
                          p={2.5}
                          sx={{
                            borderRadius: "lg",
                            border: "1px solid",
                            borderColor: ({ palette: { grey } }) => grey[200],
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "info.main",
                              transform: "translateX(8px)",
                            },
                          }}
                        >
                          <MKTypography variant="h6" mb={0.5}>
                            Friday
                          </MKTypography>
                          <MKTypography variant="body2" color="text" sx={{ fontSize: "1rem" }}>
                            2:30 PM - 10:00 PM
                          </MKTypography>
                        </MKBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MKBox
                          p={2.5}
                          sx={{
                            borderRadius: "lg",
                            border: "1px solid",
                            borderColor: ({ palette: { grey } }) => grey[200],
                            background: ({ palette: { grey } }) => grey[100],
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "info.main",
                              transform: "translateX(8px)",
                            },
                          }}
                        >
                          <MKTypography variant="h6" mb={0.5}>
                            Monday
                          </MKTypography>
                          <MKTypography variant="body2" color="text" sx={{ fontSize: "1rem" }}>
                            Closed
                          </MKTypography>
                        </MKBox>
                      </Grid>
                    </Grid>

                    <MKBox mt={4}>
                      <MKTypography variant="h4" mb={2}>
                        Location
                      </MKTypography>
                      <MKTypography variant="body2" color="text" mb={3} sx={{ fontSize: "1rem" }}>
                        35 Jalan Eco Ardence D U12/36D, Shah Alam
                      </MKTypography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <MKButton
                            component="a"
                            href="https://ul.waze.com/ul?venue_id=66519071.665059637.37856002&overview=yes&utm_campaign=default&utm_source=waze_website&utm_medium=lm_share_location"
                            target="_blank"
                            color="info"
                            fullWidth
                            sx={{
                              backgroundColor: "#33ccff",
                              color: "white",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                backgroundColor: "#33ccff",
                                boxShadow: "0 8px 20px -6px rgba(51,204,255,0.4)",
                              },
                            }}
                          >
                            <Icon>directions</Icon>&nbsp; Navigate with Waze
                          </MKButton>
                        </Grid>
                        <Grid item xs={12}>
                          <MKSocialButton
                            component="a"
                            href="https://www.instagram.com/comienzomy?igsh=MWhjYmh5Y2R0dnhlMg=="
                            target="_blank"
                            color="dark"
                            fullWidth
                            sx={{
                              backgroundColor: "black",
                              color: "white",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                backgroundColor: "black",
                                boxShadow: "0 8px 20px -6px rgba(0,0,0,0.4)",
                              },
                            }}
                          >
                            <Icon>instagram</Icon>&nbsp; Follow us on Instagram
                          </MKSocialButton>
                        </Grid>
                      </Grid>
                    </MKBox>
                  </MKBox>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <MKBox mt={8} mb={4} textAlign="center">
            <MKTypography variant="h2" color="info" mb={1}>
              Featured Menu
            </MKTypography>
            <MKTypography variant="body1" color="text" mb={4} sx={{ fontSize: "1.1rem" }}>
              Discover our signature dishes that blend Mexican and Italian flavors
            </MKTypography>
          </MKBox>

          <Grid container spacing={3}>
            {menuItems.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.name}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px) scale(1.02)",
                      boxShadow: "0 12px 28px rgba(0,0,0,0.1)",
                    },
                  }}
                  onClick={() => setSelectedMenuItem(item)}
                >
                  <MKBox
                    position="relative"
                    borderRadius="lg"
                    shadow="md"
                    width="100%"
                    height="240px"
                    overflow="hidden"
                  >
                    {item.badge && (
                      <MKBadge
                        variant="contained"
                        color="info"
                        badgeContent={item.badge}
                        container
                        circular
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          zIndex: 2,
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                          py: 1,
                          px: 1.5,
                        }}
                      />
                    )}
                    <MKBox
                      component="img"
                      src={item.image}
                      alt={item.name}
                      width="100%"
                      height="100%"
                      sx={{
                        objectFit: "cover",
                        objectPosition: "center",
                        transition: "transform 0.6s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    />
                    <MKBox
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      sx={{
                        background:
                          "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 100%)",
                      }}
                    />
                  </MKBox>
                  <MKBox p={3} flexGrow={1} display="flex" flexDirection="column">
                    <MKTypography variant="h5" mb={1} sx={{ fontWeight: 600 }}>
                      {item.name}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      color="text"
                      mb={2}
                      flexGrow={1}
                      sx={{ fontSize: "0.95rem" }}
                    >
                      {item.description}
                    </MKTypography>
                    <MKBox display="flex" justifyContent="space-between" alignItems="center">
                      <MKTypography
                        variant="h6"
                        color="info"
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.1rem",
                        }}
                      >
                        {item.price}
                      </MKTypography>
                      <MKTypography
                        variant="caption"
                        color="text"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: 500,
                        }}
                      >
                        {item.category}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
      <ReservationForm open={openReservation} onClose={() => setOpenReservation(false)} />
      <MenuItemDialog
        open={selectedMenuItem !== null}
        onClose={() => setSelectedMenuItem(null)}
        menuItem={selectedMenuItem}
      />
    </>
  );
}

export default RestaurantLanding;
